/**
 * Event Ticketing Service — Event in a Box, Phase 1a.
 * Thin client over the atomic provisioner RPC provision_event_ticketing and the
 * event_ticketing_provisioning identity row, plus the separate "open venue
 * check-in" action (the provisioner leaves public check-in DISABLED by default).
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export type AwardKind = "drawing" | "voucher" | "gift";
// The kind persisted/sent to the provisioner. "none" = ticket-only (no upgrade
// reward): the $0 admission ticket is issued but no reward offer is attached and
// the ticket does not auto-escalate; operators upgrade manually on the event page.
// It is NOT an award TYPE (never shown in the award-type dropdown) — it's the
// "Ticket only" top-level choice mapped at the provisioning boundary.
export type ProvisionAwardKind = AwardKind | "none";
export type EscalationEndMode = "end_of_day" | "fixed";

/** Operator-facing labels (auditor UX note: don't surface raw kinds). */
export const AWARD_KIND_LABELS: Record<AwardKind, string> = {
  drawing: "Door prize drawing",
  voucher: "$ voucher reward",
  gift: "Physical gift",
};

export interface ProvisionTicketingParams {
  eventId: UUID;
  awardKind: ProvisionAwardKind;
  // Null for ticket-only ('none'); required for a real award.
  awardName: string | null;
  awardValue?: number | null; // required for 'voucher'
  escalationEndMode?: EscalationEndMode;
  escalationEndAt?: string | null; // ISO, required when mode='fixed'
  dryRun?: boolean;
  // Per-eligibility override: a NON-member/guest gets this award instead of the member award.
  // Omit (null) to have no guest tier (single-tier — everyone gets the member award).
  guestAwardKind?: AwardKind | null;
  guestAwardName?: string | null;
  guestAwardValue?: number | null;
}

export interface ProvisionResult {
  dry_run?: boolean;
  provisioned?: boolean;
  created?: boolean;
  locked?: boolean;
  blocked?: boolean;
  reason?: string;
  provisioning_id?: string;
  ticket_offer_id?: string;
  award_offer_id?: string;
  event_offer_id?: string;
  award_kind?: string;
  award_offer_type?: string;
  is_non_monetary?: boolean;
  self_checkin_disabled?: boolean;
  self_checkin_default_applied?: boolean;
  self_checkin_will_default_disabled?: boolean;
  blocked_already_ticketed?: boolean;
  check_in_note?: string;
  [k: string]: unknown;
}

export interface EventTicketingProvisioning {
  id: string;
  event_id: string;
  ticket_offer_id: string | null;
  award_offer_id: string | null;
  event_offer_id: string | null;
  award_kind: ProvisionAwardKind;
  // Null when award_kind='none' (ticket-only).
  award_name: string | null;
  award_value: number | null;
  guest_award_offer_id: string | null;
  // Derived from the guest award offer for seeding the editor (not stored on the row).
  guest_award_kind?: AwardKind | null;
  guest_award_name?: string | null;
  guest_award_value?: number | null;
  status: "configured" | "locked";
  registration_mode: "invite_gated" | "open";
  open_issuance_cap: number | null;
  issued_count: number;
  created_at: string;
  updated_at: string;
}

/** Call the provisioner. Pass dryRun to preview without writing. */
export async function provisionEventTicketing(
  params: ProvisionTicketingParams,
): Promise<ProvisionResult> {
  const { data, error } = await supabase.rpc("provision_event_ticketing", {
    p_event_id: params.eventId,
    p_award_kind: params.awardKind,
    p_award_name: params.awardName,
    p_award_value: params.awardValue ?? null,
    p_escalation_end_mode: params.escalationEndMode ?? "end_of_day",
    p_escalation_end_at: params.escalationEndAt ?? null,
    p_dry_run: params.dryRun ?? false,
    p_guest_award_kind: params.guestAwardKind ?? null,
    p_guest_award_name: params.guestAwardName ?? null,
    p_guest_award_value: params.guestAwardValue ?? null,
  });
  if (error) {
    throw error;
  }
  return (data ?? {}) as ProvisionResult;
}

/** Resulting check-in state of a walk-in-only provisioning, for verification. */
export interface WalkInOnlyResult {
  provisioned: boolean;
  event_id: UUID;
  /** MUST be false — walk-in-only exists to keep self check-in ON. */
  self_checkin_disabled: boolean;
  checkin_enabled: boolean;
  allow_walk_ins: boolean;
  registration_required: boolean;
  expected_capacity: number | null;
}

/**
 * Provision a WALK-IN-ONLY event in one authorized, atomic server op: provisions ticketing,
 * ENABLES public self check-in (provision_event_ticketing alone disables it), sets
 * allow_walk_ins=true / registration_required=false, and persists the optional capacity
 * (null ⇒ uncapped). No launch record / flight / invitation. See migration
 * 2026-08-15-030-eib-walk-in-only-provision.
 */
export async function provisionWalkInOnly(params: {
  eventId: UUID;
  awardKind: ProvisionAwardKind;
  awardName: string | null;
  awardValue: number | null;
  guestAwardKind?: ProvisionAwardKind | null;
  guestAwardName?: string | null;
  guestAwardValue?: number | null;
  expectedCapacity?: number | null;
}): Promise<WalkInOnlyResult> {
  const { data, error } = await supabase.rpc("eib_provision_walk_in_only", {
    p_event_id: params.eventId,
    p_award_kind: params.awardKind,
    p_award_name: params.awardName,
    p_award_value: params.awardValue ?? null,
    p_guest_award_kind: params.guestAwardKind ?? null,
    p_guest_award_name: params.guestAwardName ?? null,
    p_guest_award_value: params.guestAwardValue ?? null,
    p_expected_capacity: params.expectedCapacity ?? null,
  });
  if (error) {
    throw error;
  }
  const res = (data ?? {}) as WalkInOnlyResult;
  // Runtime-validate the success shape (re-audit BLOCKER): the RPC returns a jsonb object; a blocked
  // or partial response (e.g. provisioning refused) must never be cast into a success-shaped result
  // and reported as a ready walk-in event. Every field must be exactly right.
  if (
    res.provisioned !== true ||
    res.checkin_enabled !== true ||
    res.self_checkin_disabled !== false ||
    res.allow_walk_ins !== true ||
    res.registration_required !== false
  ) {
    throw new Error(
      "Walk-in provisioning did not fully complete (check-in not enabled or provisioning refused).",
    );
  }
  return res;
}

/** Read the existing provisioning row for an event (null if not yet provisioned). */
export async function getEventTicketingProvisioning(
  eventId: UUID,
): Promise<EventTicketingProvisioning | null> {
  const { data, error } = await supabase
    .from("event_ticketing_provisioning")
    .select("*")
    .eq("event_id", eventId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  const row = (data as EventTicketingProvisioning | null) ?? null;
  // Derive the guest reward's kind/name/value from its offer so the editor can seed + re-send
  // it (the row stores only the pointer; without seeding, saving would clear the guest tier).
  if (row?.guest_award_offer_id) {
    const { data: g, error: gErr } = await supabase
      .from("offers")
      .select("name, value_amount, offer_type, is_non_monetary_reward")
      .eq("id", row.guest_award_offer_id)
      .maybeSingle();
    // Only derive when the read succeeded. On error the guest_award_* stay undefined; the panel
    // seeds the toggle from guest_award_offer_id so the tier isn't silently cleared on save.
    if (!gErr && g) {
      let gk: AwardKind = "voucher";
      if (g.offer_type === "EVENT_DRAWING") {
        gk = "drawing";
      } else if (g.is_non_monetary_reward) {
        gk = "gift";
      }
      row.guest_award_kind = gk;
      row.guest_award_name = (g.name as string) ?? null;
      row.guest_award_value = (g.value_amount as number | null) ?? null;
    }
  }
  return row;
}

/**
 * Open (or close) public venue check-in — the SEPARATE deliberate action the
 * provisioner intentionally does NOT perform. Provisioning leaves
 * self_checkin_disabled=true; this flips it. Goes through a guarded RPC
 * (same role/ownership gate as provisioning) rather than a direct events
 * update, per the external-audit hardening rec.
 */
export async function setEventCheckinEnabled(
  eventId: UUID,
  enabled: boolean,
): Promise<void> {
  const { error } = await supabase.rpc("set_event_checkin_enabled", {
    p_event_id: eventId,
    p_enabled: enabled,
  });
  if (error) {
    throw error;
  }
}

// ===========================================================================
// EIB Gate 5 — invite-gating operator controls
// ===========================================================================

export type RegistrationMode = "invite_gated" | "open";

/**
 * Set the event's registration mode (invite_gated default / open + cap). Goes through the guarded
 * set_event_registration_mode RPC (admin/superadmin or owning manager). Requires the event to be
 * provisioned first.
 */
export async function setEventRegistrationMode(
  eventId: UUID,
  mode: RegistrationMode,
  openCap?: number | null,
): Promise<void> {
  const { error } = await supabase.rpc("set_event_registration_mode", {
    p_event_id: eventId,
    p_mode: mode,
    p_open_issuance_cap: mode === "open" ? (openCap ?? null) : null,
  });
  if (error) {
    throw error;
  }
}

/** The event's PUBLISHED landing page id (needed to build invite links). Null if none published. */
export async function getEventLandingPageId(
  eventId: UUID,
): Promise<string | null> {
  const { data, error } = await supabase
    .from("event_landing_pages")
    .select("id")
    .eq("event_id", eventId)
    .eq("is_published", true)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data?.id as string | undefined) ?? null;
}

export interface InviteLinkResult {
  url: string | null;
  /** true when a token already existed (raw token unrecoverable — revoke to regenerate). */
  reused: boolean;
  /** on reused: the existing token's status ('active' | 'consumed' | 'revoked'). */
  status?: string;
  error?: string;
}

/**
 * Mint (or report existing) a per-recipient invite link for one contact. The raw token is revealed
 * ONLY here, to the authorized operator, and embedded directly in the returned URL — it is never
 * stored or logged. If a token already exists (reused), the raw value can't be recovered (it is
 * hashed at rest); the caller should offer Revoke → regenerate.
 */
export async function generateInviteLink(
  eventId: UUID,
  contactId: UUID,
  landingPageId: string,
): Promise<InviteLinkResult> {
  const { data, error } = await supabase.rpc("mint_event_invite_token", {
    p_event_id: eventId,
    p_contact_id: contactId,
  });
  if (error) {
    return { url: null, reused: false, error: error.message };
  }
  const token = (data?.token as string | null) ?? null;
  if (!token) {
    return {
      url: null,
      reused: !!data?.reused,
      status: (data?.status as string | undefined) ?? undefined,
      error: "already_generated",
    };
  }
  const base = window.location.origin;
  return {
    url: `${base}/register/${landingPageId}?invite=${token}`,
    reused: false,
  };
}

/** Revoke a contact's active invite token (incident response / regenerate). */
export async function revokeInviteToken(
  eventId: UUID,
  contactId: UUID,
): Promise<{ ok: boolean; reason?: string }> {
  const { data, error } = await supabase.rpc("revoke_event_invite_token", {
    p_event_id: eventId,
    p_contact_id: contactId,
  });
  if (error) {
    return { ok: false, reason: error.message };
  }
  return {
    ok: !!data?.ok,
    reason: (data?.reason as string | undefined) ?? undefined,
  };
}

/** The provisioning row's online-registration cap, for the event Overview (null = not provisioned). */
export interface EventRegistrationCapRow {
  registration_mode: "invite_gated" | "open";
  open_issuance_cap: number | null;
  issued_count: number;
}

/**
 * Lightweight read of the provisioning row's registration cap. Same RLS as
 * getEventTicketingProvisioning (SELECT to authenticated, field-manager scoped),
 * without the guest-reward offer lookup.
 */
export async function getEventRegistrationCap(
  eventId: UUID,
): Promise<EventRegistrationCapRow | null> {
  const { data, error } = await supabase
    .from("event_ticketing_provisioning")
    .select("registration_mode, open_issuance_cap, issued_count")
    .eq("event_id", eventId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data as EventRegistrationCapRow | null) ?? null;
}
